import React, { useState, useEffect } from 'react';
import BaseLayout from '../../components/layout/BaseLayout';
import CaseStudyHeroSection from '../../components/sections/CaseStudyHero/CaseStudyHeroSection';
import CaseStudyTemplate from '../../components/templates/CaseStudyTemplate';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import SEOMetaTags from '../../components/SEO/SEOMetaTags';

// Import case study images
import smartMedicsIntro from '../../assets/images/cases/smartmedics-intro.png';
import smartMedicsDescription from '../../assets/images/cases/smartmedics-description.png';
import smartMedicsTech from '../../assets/images/cases/smartmedics-tech.png';
import smartMedicsAudit from '../../assets/images/cases/smartmedics-audit.png';
import diagnozujILeczIntro from '../../assets/images/cases/dil-intro.png';
import diagnozujILeczDescription from '../../assets/images/cases/dil-description.png';
import diagnozujILeczTech from '../../assets/images/cases/dil-tech.png';
import diagnozujILeczAudit from '../../assets/images/cases/dil-audit.png';
import diagnozujILeczAuthor from '../../assets/images/clients/Angelini-Mateusz-Patorski.png';
import avatarImage from '../../assets/images/avatar.svg';
import photoYakov from '../../assets/images/team/photo-yakov.png';
import photoAdam from '../../assets/images/team/photo-adam.png';
import photoMateusz from '../../assets/images/team/photo-mateusz.png';
import CalculatorSection from '../../components/sections/Calculator/CalculatorSection';
import CaseStudySection from '../../components/sections/CaseStudy/CaseStudySection';

const CaseStudiesPage: React.FC = () => {
    const [activeTabId, setActiveTabId] = useState<string>('diagnozujILecz');
    const { hash, search } = useLocation();
    const { t } = useTranslation();

    // Move caseStudies inside component to access t() function
    const caseStudies = {
        diagnozujILecz: {
            id: "diagnozujILecz",
            title: t("diagnozujILecz.title"),
            subtitle: t("diagnozujILecz.subtitle"),
            projectDescription: t("diagnozujILecz.projectDescription"),
            tasks: [
                t("diagnozujILecz.task1"),
                t("diagnozujILecz.task2"),
                t("diagnozujILecz.task3"),
                t("diagnozujILecz.task4")
            ],
            introImage: diagnozujILeczIntro,
            introImageSource: t("diagnozujILecz.introImageSource"),
            introImageSourceUrl: "",
            description: {
                title: t("diagnozujILecz.description.title"),
                text: [
                    t("diagnozujILecz.description.text1"),
                    t("diagnozujILecz.description.text2"),
                    t("diagnozujILecz.description.text3")
                ],
                image: diagnozujILeczDescription,
                imageSource: t("diagnozujILecz.description.imageSource"),
                imageSourceUrl: ""
            },
            technology: {
                title: t("diagnozujILecz.technology.title"),
                text: [
                    t("diagnozujILecz.technology.text1"),
                    t("diagnozujILecz.technology.text2")
                ],
                image: diagnozujILeczTech,
                imageSource: t("diagnozujILecz.technology.imageSource"),
                imageSourceUrl: ""
            },
            expert: {
                title: t("diagnozujILecz.expert.title"),
                description: t("diagnozujILecz.expert.description"),
                quote: [
                    t("diagnozujILecz.expert.quote"),
                ],
                author: {
                    name: "Mateusz Chechliński",
                    role: t("team.role.ceo"),
                    image: photoMateusz
                }
            },
            guarantees: {
                title: t("diagnozujILecz.guarantees.title"),
                description: t("diagnozujILecz.guarantees.description"),
                items: [
                    {
                        title: t("diagnozujILecz.guarantees.item1.title"),
                        description: t("diagnozujILecz.guarantees.item1.description")
                    },
                    {
                        title: t("diagnozujILecz.guarantees.item2.title"),
                        description: t("diagnozujILecz.guarantees.item2.description")
                    },
                    {
                        title: t("diagnozujILecz.guarantees.item3.title"),
                        description: t("diagnozujILecz.guarantees.item3.description")
                    }
                ]
            },
            recommendations: {
                title: t("diagnozujILecz.recommendations.title"),
                description: t("diagnozujILecz.recommendations.description"),
                testimonial: {
                    quote: [
                        t("diagnozujILecz.recommendations.quote1")
                    ],
                    author: {
                        name: "Diagnozuj i Lecz",
                        role: "Project Manager",
                        image: diagnozujILeczAuthor
                    }
                }
            },
            education: {
                title: t("diagnozujILecz.education.title"),
                description: t("diagnozujILecz.education.description"),
                quote: {
                    text: t("diagnozujILecz.education.quote"),
                    author: {
                        image: photoAdam
                    }
                }
            },
            audit: {
                title: t("diagnozujILecz.audit.title"),
                description: t("diagnozujILecz.audit.description"),
                additionalDescription: t("diagnozujILecz.audit.additionalDescription"),
                type: "image" as const,
                image: diagnozujILeczAudit
            }
        },
        smartMedics: {
            id: "smartMedics",
            title: t("smartMedics.title"),
            subtitle: t("smartMedics.subtitle"),
            projectDescription: t("smartMedics.projectDescription"),
            tasks: [
                t("smartMedics.task1"),
                t("smartMedics.task2"),
                t("smartMedics.task3"),
                t("smartMedics.task4")
            ],
            introImage: smartMedicsIntro,
            introImageSource: t("smartMedics.introImageSource"),
            introImageSourceUrl: "https://smartmedics.pl",
            description: {
                title: t("smartMedics.description.title"),
                text: [
                    t("smartMedics.description.text1"),
                    t("smartMedics.description.text2"),
                    t("smartMedics.description.text3")
                ],
                image: smartMedicsDescription,
                imageSource: t("smartMedics.description.imageSource"),
                imageSourceUrl: "https://smartmedics.pl"
            },
            technology: {
                title: t("smartMedics.technology.title"),
                text: [
                    t("smartMedics.technology.text1"),
                    t("smartMedics.technology.text2")
                ],
                image: smartMedicsTech,
                imageSource: t("smartMedics.technology.imageSource"),
                imageSourceUrl: "https://smartmedics.pl"
            },
            expert: {
                title: t("smartMedics.expert.title"),
                description: t("smartMedics.expert.description"),
                quote: [
                    t("smartMedics.expert.quote1"),
                    t("smartMedics.expert.quote2"),
                    t("smartMedics.expert.quote3")
                ],
                author: {
                    name: "Mateusz Chechliński",
                    role: t("team.role.ceo"),
                    image: photoMateusz
                }
            },
            guarantees: {
                title: t("smartMedics.guarantees.title"),
                description: t("smartMedics.guarantees.description"),
                items: [
                    {
                        title: t("smartMedics.guarantees.item1.title"),
                        description: t("smartMedics.guarantees.item1.description")
                    },
                    {
                        title: t("smartMedics.guarantees.item2.title"),
                        description: t("smartMedics.guarantees.item2.description")
                    },
                    {
                        title: t("smartMedics.guarantees.item3.title"),
                        description: t("smartMedics.guarantees.item3.description")
                    },
                    {
                        title: t("smartMedics.guarantees.item4.title"),
                        description: t("smartMedics.guarantees.item4.description")
                    },
                    {
                        title: t("smartMedics.guarantees.item5.title"),
                        description: t("smartMedics.guarantees.item5.description")
                    },
                    {
                        title: t("smartMedics.guarantees.item6.title"),
                        description: t("smartMedics.guarantees.item6.description")
                    },
                ]
            },
            recommendations: {
                title: t("smartMedics.recommendations.title"),
                description: t("smartMedics.recommendations.description"),
                testimonial: {
                    quote: [
                        t("smartMedics.recommendations.quote1"),
                        t("smartMedics.recommendations.quote2"),
                    ],
                    author: {
                        name: "SmartMedics",
                        role: "Project Manager",
                        image: avatarImage
                    }
                }
            },
            education: {
                title: t("smartMedics.education.title"),
                description: t("smartMedics.education.description"),
                quote: {
                    text: t("smartMedics.education.quote"),
                    author: {
                        image: photoYakov
                    }
                }
            },
            audit: {
                title: t("smartMedics.audit.title"),
                description: t("smartMedics.audit.description"),
                additionalDescription: t("smartMedics.audit.additionalDescription"),
                type: "image" as const,
                image: smartMedicsAudit
            }
        },
    };

    useEffect(() => {
        // Check hash first
        if (hash.startsWith('#') && hash.length > 1) {
            const hashValue = hash.substring(1);
            if (hashValue === 'smartMedics' || hashValue === 'diagnozujILecz') {
                setActiveTabId(hashValue);
                
                // Add a slight delay to ensure the component is rendered before scrolling
                setTimeout(() => {
                    const contentSection = document.getElementById('case-study-content');
                    if (contentSection) {
                        const elementPosition = contentSection.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset - 64;
                        window.scrollTo({
                            top: offsetPosition,
                            behavior: 'smooth'
                        });
                    }
                }, 300);
            }
        } 
        // Also check URL query parameters
        else if (search) {
            const searchParams = new URLSearchParams(search);
            const tabParam = searchParams.get('tab');
            if (tabParam === 'smartMedics' || tabParam === 'diagnozujILecz') {
                setActiveTabId(tabParam);
                
                // Add a slight delay to ensure the component is rendered before scrolling
                setTimeout(() => {
                    const contentSection = document.getElementById('case-study-content');
                    if (contentSection) {
                        const elementPosition = contentSection.getBoundingClientRect().top;
                        const offsetPosition = elementPosition + window.pageYOffset - 64;
                        window.scrollTo({
                            top: offsetPosition,
                            behavior: 'smooth'
                        });
                    }
                }, 300);
            }
        }
    }, [hash, search]);

    const tabs = [
        {
            id: 'diagnozujILecz',
            title: t("caseStudies.tabs.diagnozujILecz")
        },
        {
            id: 'smartMedics',
            title: t("caseStudies.tabs.smartMedics")
        }
    ];

    // SEO meta tags for each case study
    const seoMetaTags = {
        diagnozujILecz: {
            title: {
                pl: "Appotype Case Study: Aplikacja \"Diagnozuj i Lecz\" dla Angelini Pharma",
                en: "Appotype Case Study: \"Diagnozuj i Lecz\" App for Angelini Pharma"
            },
            description: {
                pl: "Appotype Case Study: Jak stworzyliśmy aplikację \"Diagnozuj i Lecz\" (Flutter) dla Angelini Pharma wspierającą diagnostykę depresji. Poznaj nasze podejście i technologię.",
                en: "Appotype Case Study: How we built the \"Diagnozuj i Lecz\" app (Flutter) for Angelini Pharma to support depression diagnosis. Learn about our approach & technology."
            },
            canonicalUrl: "/realizacje#diagnozujILecz"
        },
        smartMedics: {
            title: {
                pl: "Appotype Case Study: Aplikacja Medyczna SmartMedics z Integracją EKG",
                en: "Appotype Case Study: SmartMedics Medical App with ECG Integration"
            },
            description: {
                pl: "Appotype Case Study: Przebudowa i rozwój aplikacji medycznej SmartMedics. Integracja EKG (BLE), chmura, bezpieczeństwo HIPAA/RODO dla monitoringu pacjentów.",
                en: "Appotype Case Study: Rebuilding & developing the SmartMedics medical app. ECG integration (BLE), cloud, HIPAA/GDPR security for patient monitoring."
            },
            canonicalUrl: "/realizacje#smartMedics"
        }
    };

    return (
        <>
            <SEOMetaTags
                title={seoMetaTags[activeTabId as keyof typeof seoMetaTags].title}
                description={seoMetaTags[activeTabId as keyof typeof seoMetaTags].description}
                canonicalUrl={seoMetaTags[activeTabId as keyof typeof seoMetaTags].canonicalUrl}
            />
            <BaseLayout>
                <CaseStudyHeroSection
                    tabs={tabs}
                    activeTabId={activeTabId}
                    onTabChange={setActiveTabId}
                />
                <div id="case-study-content">
                    <CaseStudyTemplate 
                        key={`case-study-${activeTabId}`} 
                        content={caseStudies[activeTabId as keyof typeof caseStudies]} 
                    />
                </div>
                <CalculatorSection />
                <CaseStudySection title={t("medtech.caseStudy.title")} />
            </BaseLayout>
        </>
    );
};

export default CaseStudiesPage;